/**
 * DeploymentSelfTestCard.js — Iter-411v
 *
 * A compact self-test card designed to slot into the existing
 * AdminHealthDashboard. NOT a new page — user directive was
 * "no complicated Admin page". One button, ten checks, clear result.
 *
 * Exposes a single admin action: "Run Deployment Self-Test". The
 * server-side check inventory lives in
 * backend/services/deployment_self_test.py.
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, CheckCircle2, AlertTriangle, XCircle, PlayCircle, ShieldCheck,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const STATUS_ICON = {
  PASS:    <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
  WARNING: <AlertTriangle className="w-4 h-4 text-amber-500" />,
  FAIL:    <XCircle className="w-4 h-4 text-red-500" />,
};
const STATUS_ROW_BG = {
  PASS:    'bg-emerald-50/50 border-emerald-200',
  WARNING: 'bg-amber-50/60 border-amber-200',
  FAIL:    'bg-red-50/70 border-red-200',
};
const OVERALL_BAR = {
  PASS:    'bg-emerald-500 text-white',
  WARNING: 'bg-amber-500 text-white',
  FAIL:    'bg-red-600 text-white',
};

export default function DeploymentSelfTestCard() {
  const [running, setRunning]  = useState(false);
  const [report,  setReport]   = useState(null);

  const run = useCallback(async () => {
    setRunning(true);
    try {
      const r = await axios.get(`${API}/admin/deployment/self-test`, AUTH());
      setReport(r.data);
      if (r.data.overall === 'FAIL') {
        toast.error(`Deployment self-test FAIL — ${r.data.counts.fail} critical check(s) failed`);
      } else if (r.data.overall === 'WARNING') {
        toast.warning(`Deployment self-test PASS with ${r.data.counts.warning} warning(s)`);
      } else {
        toast.success('Deployment self-test PASS — all contracts green');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Self-test endpoint unavailable');
    } finally {
      setRunning(false);
    }
  }, []);

  return (
    <Card
      className="p-4 border border-slate-200 shadow-sm"
      data-testid="deployment-self-test-card"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-cyan-600" />
            <h3 className="text-base font-semibold text-slate-900">
              Deployment Self-Test
            </h3>
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            Runs the critical post-deploy contract checks (attendance
            enum, auth callback, market status, background-check gate,
            reliability protections). Read-only or self-cleaning.
          </p>
        </div>
        <Button
          size="sm"
          onClick={run}
          disabled={running}
          className="bg-cyan-600 hover:bg-cyan-700 text-white flex-shrink-0"
          data-testid="deployment-self-test-run-btn"
        >
          {running
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running…</>
            : <><PlayCircle className="w-4 h-4 mr-2" />Run Self-Test</>}
        </Button>
      </div>

      {report && (
        <div className="space-y-2" data-testid="deployment-self-test-report">
          <div
            className={`rounded-md px-3 py-2 text-sm font-semibold flex items-center justify-between ${OVERALL_BAR[report.overall]}`}
            data-testid="deployment-self-test-overall"
          >
            <span>Overall: {report.overall}</span>
            <span className="text-xs font-normal opacity-90">
              {report.counts.pass} PASS · {report.counts.warning} WARN · {report.counts.fail} FAIL · {report.duration_ms}ms
            </span>
          </div>
          <ul className="space-y-1">
            {report.checks.map((c) => (
              <li
                key={c.name}
                className={`flex items-start gap-2 rounded-md border px-3 py-2 ${STATUS_ROW_BG[c.status] || 'bg-slate-50 border-slate-200'}`}
                data-testid={`deployment-check-${c.name}`}
              >
                <span className="mt-0.5">{STATUS_ICON[c.status] || null}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                    <span>{c.name}</span>
                    {!c.critical && (
                      <span className="text-[10px] uppercase tracking-wide text-slate-400 border border-slate-300 rounded px-1">
                        non-critical
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 break-words">
                    {c.detail}
                  </div>
                </div>
                <span className="text-[10px] text-slate-400 flex-shrink-0">
                  {c.duration_ms}ms
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
